import {
  arrayRemove, arrayUnion, collection, doc, getDoc, getDocs, query, setDoc, updateDoc, where,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { chunk, docsWithId, withId } from './db';

const usersCollection = () => collection(db, 'users');
const userRef = (id) => doc(db, 'users', id);

/** One profile, or null. The signed-in user's own profile comes from useAuth().profile. */
export async function getUser(id) {
  if (!id) return null;
  return withId(await getDoc(userRef(id)));
}

/** Several profiles by ID, in the same order (missing ones are null). */
export function getUsers(ids) {
  return Promise.all(ids.map((id) => getUser(id)));
}

/** Every profile. Used by search and autocomplete; replace with a search index at scale. */
export async function listUsers() {
  return docsWithId(await getDocs(usersCollection()));
}

export function updateUser(id, data) {
  return updateDoc(userRef(id), data);
}

/** Append a shout-out to someone's profile. */
export function addEndorsement(userId, endorsement) {
  return updateDoc(userRef(userId), { endorsements: arrayUnion(endorsement) });
}

/** Merge fields into a profile (creating nothing new: profiles are created by createAccount). */
export function mergeUser(id, data) {
  return setDoc(userRef(id), data, { merge: true });
}

/** First profile with this exact email, or null. */
export async function findUserByEmail(email) {
  if (!email) return null;
  const snapshot = await getDocs(query(usersCollection(), where('email', '==', email)));
  return snapshot.empty ? null : docsWithId(snapshot)[0];
}

/** First profile with this exact username, or null. */
export async function findUserByUsername(username) {
  if (!username) return null;
  const snapshot = await getDocs(query(usersCollection(), where('username', '==', username)));
  return snapshot.empty ? null : docsWithId(snapshot)[0];
}

/** Profiles with these exact usernames (any order; unknown usernames are skipped). */
export async function getUsersByUsernames(usernames) {
  const unique = [...new Set(usernames.filter(Boolean))];
  const batches = await Promise.all(chunk(unique, 30).map((names) => getDocs(query(usersCollection(), where('username', 'in', names)))));
  return batches.flatMap(docsWithId);
}

/** Profiles whose email or username exactly equals `term`. */
export async function findUsersByEmailOrUsername(term) {
  const [byEmail, byUsername] = await Promise.all([
    getDocs(query(usersCollection(), where('email', '==', term))),
    getDocs(query(usersCollection(), where('username', '==', term))),
  ]);
  const byId = new Map();
  [...docsWithId(byEmail), ...docsWithId(byUsername)].forEach((user) => byId.set(user.id, user));
  return [...byId.values()];
}

/** Add or remove a post from the user's saved posts. */
export function setPostSaved(userId, postId, saved) {
  return updateUser(userId, { savedPosts: saved ? arrayUnion(postId) : arrayRemove(postId) });
}

/** Add or remove someone from a business's roster. */
export function setRosterMember(userId, memberId, onRoster) {
  return updateUser(userId, { roster: onRoster ? arrayUnion(memberId) : arrayRemove(memberId) });
}

// ─── Private profile fields (owner and admins only) ─────────────────

const privateRef = (id) => doc(db, 'users', id, 'private', 'profile');

export async function getPrivateProfile(id) {
  const snap = await getDoc(privateRef(id));
  return snap.exists() ? snap.data() : {};
}

export function savePrivateProfile(id, data) {
  return setDoc(privateRef(id), data, { merge: true });
}
